import type { MaterialNames } from "@/components/material_symbol_names";
import {
  formatCatalogValue,
  formatCatalogValues,
  formatRankedLabels,
  NO_DESIRED_CHANGES_CONSULTANT_LABEL
} from "./careerIntentCatalog";
import { CAREER_PLAN_STATUS } from "./careerPlanDocument";
import { indexCompletionPercent, type CareerPlanAssessment, type CareerPlanGoal } from "./careerPlanHistory";

export type HubCareerGoalRow = {
  label: string;
  value: string;
  iconName: MaterialNames;
};

export type ExperienceIndexCompletion = {
  answered: number;
  total: number;
  remaining: number;
  percent: number | null;
  hasAssessment: boolean;
};

export type ExperienceIndexHubState = "never_started" | "unfinished" | "finished";

export type HubCareerPlan = {
  id: string;
  title: string | null;
  status: string;
  updatedAt: string | null;
};

export const HUB_CAREER_PLAN_DRAFTS_LABEL = "Drafts / Unpublished";
export const HUB_CAREER_PLAN_PUBLISHED_LABEL = "Published";
export const HUB_CAREER_PLAN_COMPLETED_LABEL = "Completed";

const HUB_GOAL_ICONS = {
  "Target Positions": "track_changes",
  Timeframe: "schedule",
  "Areas to Improve": "trending_up",
  Motivations: "flag"
} as const satisfies Record<string, MaterialNames>;

function formatHubRankedHeadline(labels: string[]): string {
  return labels.join("\n");
}

export function getHubCareerGoalRows(goal: CareerPlanGoal | null | undefined): HubCareerGoalRow[] {
  if (!goal) return [];

  if (goal.hasNoDesiredChanges) {
    return [
      {
        label: "Direction",
        value: NO_DESIRED_CHANGES_CONSULTANT_LABEL,
        iconName: HUB_GOAL_ICONS["Target Positions"]
      }
    ];
  }

  const rows: HubCareerGoalRow[] = [];
  const targetPositions = formatRankedLabels(formatCatalogValues("targetPosition", goal.targetPositions));
  if (targetPositions.length > 0) {
    rows.push({
      label: "Target Positions",
      value: formatHubRankedHeadline(targetPositions),
      iconName: HUB_GOAL_ICONS["Target Positions"]
    });
  }

  const timeframe = goal.timeframe ? formatCatalogValue("timeframe", goal.timeframe) : "";
  if (timeframe) {
    rows.push({
      label: "Timeframe",
      value: timeframe,
      iconName: HUB_GOAL_ICONS.Timeframe
    });
  }

  const improvementAreas = formatRankedLabels(formatCatalogValues("improvementArea", goal.improvementAreas));
  if (improvementAreas.length > 0) {
    rows.push({
      label: "Areas to Improve",
      value: formatHubRankedHeadline(improvementAreas),
      iconName: HUB_GOAL_ICONS["Areas to Improve"]
    });
  }

  const motivations = formatRankedLabels(formatCatalogValues("motivation", goal.motivations));
  if (motivations.length > 0) {
    rows.push({
      label: "Motivations",
      value: formatHubRankedHeadline(motivations),
      iconName: HUB_GOAL_ICONS.Motivations
    });
  }

  if (rows.length === 0) {
    const locations = formatRankedLabels(formatCatalogValues("location", goal.locations));
    if (locations.length > 0) {
      rows.push({
        label: "States",
        value: formatHubRankedHeadline(locations),
        iconName: HUB_GOAL_ICONS["Target Positions"]
      });
    }
  }

  return rows;
}

export function getExperienceIndexHubState(completion: ExperienceIndexCompletion): ExperienceIndexHubState {
  if (completion.answered === 0) return "never_started";
  if (completion.total > 0 && completion.remaining === 0) return "finished";
  return "unfinished";
}

export function getExperienceIndexCompletion(
  assessment: Pick<CareerPlanAssessment, "answeredQuestionCount" | "totalQuestionCount"> | null | undefined
): ExperienceIndexCompletion {
  const answered = assessment?.answeredQuestionCount ?? 0;
  const total = assessment?.totalQuestionCount ?? 0;

  return {
    answered,
    total,
    remaining: Math.max(0, total - answered),
    percent: indexCompletionPercent(answered, total),
    hasAssessment: total > 0 || answered > 0
  };
}

function planUpdatedAtMs(plan: HubCareerPlan) {
  if (!plan.updatedAt) return 0;
  const ms = Date.parse(plan.updatedAt);
  return Number.isNaN(ms) ? 0 : ms;
}

function sortPlansByLastModified(plans: HubCareerPlan[]) {
  return [...plans].sort((left, right) => planUpdatedAtMs(right) - planUpdatedAtMs(left));
}

export function groupHubCareerPlans(plans: HubCareerPlan[]) {
  const drafts: HubCareerPlan[] = [];
  const published: HubCareerPlan[] = [];
  const completed: HubCareerPlan[] = [];

  for (const plan of plans) {
    if (plan.status === CAREER_PLAN_STATUS.COMPLETED) {
      completed.push(plan);
    } else if (plan.status === CAREER_PLAN_STATUS.PUBLISHED) {
      published.push(plan);
    } else {
      drafts.push(plan);
    }
  }

  return {
    drafts: sortPlansByLastModified(drafts),
    published: sortPlansByLastModified(published),
    completed: sortPlansByLastModified(completed)
  };
}
